import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { getCategories } from "./categories";
import { useAppData } from "./store";
import { Mic, Square, Save, X, Sparkles, RotateCcw, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOS = [
  { id: "entrada",  label: "Entrada",         on: "bg-emerald-500/20 border-emerald-500/50 text-emerald-300" },
  { id: "fixo",     label: "Gasto Fixo",      on: "bg-blue-500/20 border-blue-500/50 text-blue-300" },
  { id: "variavel", label: "Gasto Variável",  on: "bg-rose-500/20 border-rose-500/50 text-rose-300" },
  { id: "cartao",   label: "Cartão Crédito",  on: "bg-violet-500/20 border-violet-500/50 text-violet-300" },
];
const PAGAMENTOS = ["Pix", "Débito", "Cartão", "Dinheiro", "Boleto", "Transferência", "Corretora", "Outro"];

const TIPO_ROUTES = {
  entrada:  "/app/entradas",
  fixo:     "/app/gastos-fixos",
  variavel: "/app/gastos-variaveis",
  cartao:   "/app/cartoes",
};

// Catches all likely STT transcriptions of "Monyxx":
// Monyxx, Monyxx, Monixx, Monix, Monex, Money, Moneys, Monexs
const WAKE_WORD_RE = /\bmon(?:yxx?|ixx?|ex+|ey)\b/i;

// Same pattern used to strip the wake word from transcription/description text
const WAKE_WORD_STRIP = /^(?:mon(?:yxx?|ixx?|ex+|ey))[,.]?\s*/i;

// Credit card keywords in transcription (Portuguese STT variations)
const CREDIT_CARD_TRANS_RE = /\b(cart[aã]o\s+(?:de\s+)?cr[eé]d[i]?to|no\s+cart[aã]o|cart[aã]o\s+cr[eé]d[i]?to|cr[eé]d[i]?to)\b/i;
// Debit card keywords — take priority so "cartão de débito" doesn't match credit
const DEBIT_TRANS_RE = /\b(d[eé]bito|cart[aã]o\s+(?:de\s+)?d[eé]bito)\b/i;

// ── Silence detection config ──────────────────────────────────
const SILENCE_THRESHOLD   = 12;
const SILENCE_DURATION_MS = 2000;
const MIN_RECORD_MS       = 1500;
const VOLUME_CHECK_MS     = 100;

const pickMime = () => {
  const opts = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const m of opts) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return "";
};

// ── Phase descriptions ────────────────────────────────────────
const STATUS = {
  idle:               "Toque no microfone — diga 'Monyxx' para modo avançado ou fale diretamente",
  wakeword_listening: "Aguardando... diga 'Monyxx' ou fale diretamente",
  wakeword_active:    "Pode falar seu lançamento...",
  recording:          "Ouvindo... para automaticamente ao detectar silêncio",
  processing:         "Transcrevendo e analisando com IA...",
  retry:              "Não entendi, pode repetir?",
  preview:            "Revise os campos e confirme para salvar",
  error:              "Ops! Tente novamente.",
};

export default function VoiceLancamentoModal({ open, onOpenChange, defaultMonth }) {
  const { data: appData, addLancamento, addCartaoTx } = useAppData();
  const navigate = useNavigate();

  const [phase, setPhase]           = useState("idle");
  const [transcricao, setTranscricao] = useState("");
  const [form, setForm]             = useState(null);
  const [confianca, setConfianca]   = useState(null);
  const [recordingSec, setRecordingSec] = useState(0);
  const [volumeLevel, setVolumeLevel]   = useState(0);

  const mediaRef         = useRef(null);
  const chunksRef        = useRef([]);
  const streamRef        = useRef(null);
  const timerRef         = useRef(null);
  const audioCtxRef      = useRef(null);
  const analyserRef      = useRef(null);
  const silenceStartRef  = useRef(null);
  const recordStartRef   = useRef(null);
  const silenceCheckRef  = useRef(null);
  const hasSpeechRef     = useRef(false);
  const speechRecRef     = useRef(null);
  const wakeWordRef      = useRef(false);  // true once "Monyxx" detected
  const isStartingRef    = useRef(false);  // prevents double-click during async getUserMedia

  // ── Reset on open/close ──────────────────────────────────────
  useEffect(() => {
    if (open) {
      setPhase("idle");
      setTranscricao("");
      setForm(null);
      setConfianca(null);
      setRecordingSec(0);
      setVolumeLevel(0);
    } else {
      cleanupRecorder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanupRecorder = useCallback(() => {
    try { mediaRef.current?.stop(); }                                        catch { /* */ }
    try { speechRecRef.current?.abort?.() || speechRecRef.current?.stop(); } catch { /* */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (timerRef.current)       { clearInterval(timerRef.current);       timerRef.current = null; }
    if (silenceCheckRef.current){ clearInterval(silenceCheckRef.current); silenceCheckRef.current = null; }
    try { audioCtxRef.current?.close(); } catch { /* */ }
    mediaRef.current = null; streamRef.current = null; chunksRef.current = [];
    audioCtxRef.current = null; analyserRef.current = null; speechRecRef.current = null;
    silenceStartRef.current = null; recordStartRef.current = null;
    hasSpeechRef.current = false; wakeWordRef.current = false; isStartingRef.current = false;
  }, []);

  // ── Start recording + wake word detection ───────────────────
  const startRecording = async () => {
    // If already recording, treat click as stop
    if (mediaRef.current) { stopRecording(); return; }
    // Prevent second invocation while getUserMedia is in-flight
    if (isStartingRef.current) return;
    isStartingRef.current = true;

    if (!window.isSecureContext) { isStartingRef.current = false; toast.error("Áudio precisa de HTTPS."); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      isStartingRef.current = false;
      toast.error("Seu navegador não suporta gravação. Use Chrome ou Edge.");
      return;
    }
    try {
      // Reutilizar stream se as tracks ainda estiverem ativas.
      // No iOS, parar o stream e chamar getUserMedia de novo não é confiável —
      // o hardware pode não ser reaadquirido dentro do mesmo gesto de usuário.
      let stream = streamRef.current;
      const tracksLive = stream?.getTracks().every((t) => t.readyState === "live");
      if (!stream || !tracksLive) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
      }

      // Criar ou reutilizar AudioContext
      if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
        try { audioCtxRef.current?.close(); } catch { /* */ }
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        analyserRef.current = analyser;
      }
      // iOS suspende AudioContext quando ocioso — garantir running
      if (audioCtxRef.current.state === "suspended") {
        try { await audioCtxRef.current.resume(); } catch { /* */ }
      }
      const analyser = analyserRef.current;

      // Clonar stream para o MediaRecorder — no iOS, rec.stop() pode encerrar
      // as tracks do clone mas mantém o stream original (streamRef) vivo para reutilização
      const recStream = typeof stream.clone === "function" ? stream.clone() : stream;
      // ── MediaRecorder ──
      const mime = pickMime();
      const rec  = mime ? new MediaRecorder(recStream, { mimeType: mime }) : new MediaRecorder(recStream);
      mediaRef.current = rec;
      isStartingRef.current = false; // setup complete — mediaRef guards against re-entry now
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = handleStop;
      rec.start();
      recordStartRef.current = Date.now();
      silenceStartRef.current = null;
      hasSpeechRef.current = false;
      wakeWordRef.current = false;
      setPhase("wakeword_listening");
      setRecordingSec(0);
      setVolumeLevel(0);

      // ── Recording timer ──
      timerRef.current = setInterval(() => {
        setRecordingSec((s) => {
          if (s + 1 >= 60) { try { rec.stop(); } catch { /* */ } return 60; }
          return s + 1;
        });
      }, 1000);

      // ── Silence detection + volume feedback loop ──
      const bufLen = analyser.frequencyBinCount;
      const buf    = new Float32Array(bufLen);

      silenceCheckRef.current = setInterval(() => {
        if (!analyserRef.current || rec.state !== "recording") return;
        analyserRef.current.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < bufLen; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / bufLen) * 1000;
        setVolumeLevel(Math.min(100, Math.round(rms * 2)));

        const elapsed = Date.now() - recordStartRef.current;

        if (rms > SILENCE_THRESHOLD) {
          silenceStartRef.current = null;
          if (!hasSpeechRef.current) {
            hasSpeechRef.current = true;
            // Volume-based fallback: if Web Speech API didn't fire, switch to normal recording
            if (!wakeWordRef.current) {
              setPhase((prev) => prev === "wakeword_listening" ? "recording" : prev);
            }
          }
        } else {
          if (!silenceStartRef.current) silenceStartRef.current = Date.now();
          else if (
            hasSpeechRef.current &&
            elapsed > MIN_RECORD_MS &&
            Date.now() - silenceStartRef.current >= SILENCE_DURATION_MS
          ) {
            try { rec.stop(); } catch { /* */ }
            if (timerRef.current)       { clearInterval(timerRef.current);       timerRef.current = null; }
            if (silenceCheckRef.current){ clearInterval(silenceCheckRef.current); silenceCheckRef.current = null; }
          }
        }
      }, VOLUME_CHECK_MS);

      // ── Web Speech API — real-time wake word detection (best-effort) ──
      const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRec) {
        try {
          const sr = new SpeechRec();
          sr.continuous      = true;
          sr.interimResults  = true;
          sr.lang            = "pt-BR";
          sr.maxAlternatives = 1;

          sr.onresult = (event) => {
            const transcript = Array.from(event.results)
              .map((r) => r[0].transcript)
              .join("")
              .trim();

            if (wakeWordRef.current) return; // already detected

            if (WAKE_WORD_RE.test(transcript)) {
              // "Monyxx" detected mid-stream
              wakeWordRef.current = true;
              silenceStartRef.current = null; // give user time for the command
              hasSpeechRef.current = false;   // wait for command speech, not "Monyxx"
              setPhase("wakeword_active");
            } else if (transcript.length > 2) {
              // Speaking without wake word → normal mode
              setPhase((prev) => prev === "wakeword_listening" ? "recording" : prev);
            }
          };

          sr.onerror = () => { /* silent — MediaRecorder still captures */ };
          // Guarda referência local para evitar closure bug:
          // onend do sr antigo não pode zerar o speechRecRef da nova sessão
          sr.onend   = () => { if (speechRecRef.current === sr) speechRecRef.current = null; };
          sr.start();
          speechRecRef.current = sr;
        } catch { /* Web Speech API unavailable — volume fallback handles transition */ }
      }

    } catch (err) {
      isStartingRef.current = false;
      console.error("Microphone access error:", err);
      toast.error("Permissão de microfone negada.");
      setPhase("error");
    }
  };

  const stopRecording = () => {
    try { mediaRef.current?.stop(); } catch { /* */ }
    mediaRef.current = null;
    isStartingRef.current = false;
    // Stream e AudioContext ficam vivos para a próxima gravação dentro do mesmo modal.
    // cleanup completo (track.stop) só ocorre em cleanupRecorder, ao fechar o modal.
    try { speechRecRef.current?.abort?.() || speechRecRef.current?.stop(); } catch { /* */ }
    speechRecRef.current = null;
    if (timerRef.current)       { clearInterval(timerRef.current);       timerRef.current = null; }
    if (silenceCheckRef.current){ clearInterval(silenceCheckRef.current); silenceCheckRef.current = null; }
  };

  // ── After recording stops ────────────────────────────────────
  const handleStop = async () => {
    mediaRef.current = null;
    isStartingRef.current = false;
    // Stream e AudioContext ficam vivos para reutilização na próxima gravação.
    try { speechRecRef.current?.abort?.() || speechRecRef.current?.stop(); } catch { /* */ }
    speechRecRef.current = null;
    setVolumeLevel(0);

    const mimeType = chunksRef.current[0]?.type || "audio/webm";
    const ext = mimeType.includes("mp4") ? "mp4" : mimeType.includes("ogg") ? "ogg" : "webm";
    const blob = new Blob(chunksRef.current, { type: mimeType });
    if (blob.size < 800) {
      setPhase("idle");
      if (hasSpeechRef.current) toast.error("Gravação muito curta. Fale por pelo menos 1 segundo.");
      return;
    }

    setPhase("processing");
    try {
      const fd = new FormData();
      fd.append("audio", blob, `voice.${ext}`);
      const { data } = await axios.post(`${API}/ai/voice-lancamento`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });

      const s = data.sugestao || {};

      // Strip wake word from transcription display AND from AI-parsed description.
      // STT may transcribe "Monyxx" as "Monex", "Money", etc.
      const raw = data.transcricao || "";
      setTranscricao(raw.replace(WAKE_WORD_STRIP, "").trim() || raw);
      const cleanDesc = (s.descricao || "").replace(WAKE_WORD_STRIP, "").trim();

      // If AI couldn't extract meaningful data → retry
      if (!cleanDesc && !s.valor) {
        setPhase("retry");
        return;
      }

      // Normalise tipo — transcription takes priority over AI classification for cart/credit/debit
      // so "200 na Shopee no cartão de crédito" is NEVER saved as Gastos Variáveis.
      const rawTipo = (s.tipo || "variavel").toLowerCase();
      const transcHasCreditCard = CREDIT_CARD_TRANS_RE.test(raw) && !DEBIT_TRANS_RE.test(raw);
      const transcHasDebit      = DEBIT_TRANS_RE.test(raw);
      const aiIsCreditCard      = rawTipo.includes("cart") || rawTipo.includes("créd") || rawTipo.includes("cred");
      const tipo = (transcHasCreditCard || (!transcHasDebit && aiIsCreditCard))
        ? "cartao"
        : rawTipo.includes("entr") || rawTipo === "receita"
        ? "entrada"
        : rawTipo.includes("fix")
        ? "fixo"
        : "variavel";

      // If debit card detected in speech, force pagamento to "Débito"
      const pagamento = transcHasDebit ? "Débito" : (s.pagamento || "Pix");

      setForm({
        tipo,
        desc:      cleanDesc,
        valor:     String(s.valor ?? ""),
        data:      s.data || (defaultMonth ? `${defaultMonth}-15` : new Date().toISOString().slice(0, 10)),
        cat:       tipo === "entrada" ? null : (s.categoria || "eventuais"),
        pagamento,
        parcelas:  tipo === "cartao" ? (s.parcelas || 1) : (s.parcelas || null),
        cartaoId:  tipo === "cartao" ? (appData.cartoes?.[0]?.id || null) : null,
        pago:      false,
        repetir:   false,
      });
      setConfianca(s.confianca ?? null);
      setPhase("preview");
    } catch (err) {
      console.error("Voice API Error:", err);
      if (err.response?.status === 401)
        toast.error("Sua sessão expirou ou você não está logado.");
      else if (err.response?.status === 402)
        toast.error("Seu período de teste expirou. Faça upgrade para continuar.");
      else
        toast.error(formatApiError(err?.response?.data?.detail) || err.message);
      setPhase("error");
    }
  };

  const onTipoChange = (t) =>
    setForm((f) => ({
      ...f,
      tipo: t,
      cat:      t === "entrada" ? null : (f.cat || "eventuais"),
      cartaoId: t === "cartao" ? (f.cartaoId || appData.cartoes?.[0]?.id || null) : null,
      parcelas: t === "cartao" ? (f.parcelas || 1) : f.parcelas,
    }));

  const confirm = () => {
    if (!form) return;
    if (!form.desc?.trim()) return toast.error("Informe a descrição.");
    const valor = Number(String(form.valor).replace(",", "."));
    if (!valor || valor <= 0) return toast.error("Informe um valor válido.");

    if (form.tipo === "cartao") {
      const cid = form.cartaoId || appData.cartoes?.[0]?.id;
      if (!cid) return toast.error("Nenhum cartão cadastrado. Acesse Cartões de Crédito para adicionar.");
      const numParcelas = Math.max(1, Math.round(Number(form.parcelas) || 1));
      const valorParcela = valor / numParcelas; // addCartaoTx expects per-installment value
      addCartaoTx(cid, {
        desc:     form.desc.trim(),
        valor:    valorParcela,
        data:     form.data,
        cat:      form.cat || "eventuais",
        parcelas: `1/${numParcelas}`,
      });
      toast.success("Transação adicionada ao cartão! ✨");
      onOpenChange(false);
      navigate("/app/cartoes");
      return;
    }

    addLancamento(form.tipo, {
      desc:      form.desc.trim(),
      valor,
      data:      form.data,
      cat:       form.tipo === "entrada" ? null : form.cat,
      pagamento: form.pagamento,
      pago:      !!form.pago,
      repetir:   !!form.repetir,
      ...(form.parcelas ? { parcelas: form.parcelas } : {}),
    });
    toast.success("Lançamento criado por voz! ✨");
    onOpenChange(false);
    const route = TIPO_ROUTES[form.tipo];
    if (route) navigate(route);
  };

  const tryAgain = () => {
    wakeWordRef.current = false;
    setPhase("idle"); setTranscricao(""); setForm(null);
    setConfianca(null); setRecordingSec(0); setVolumeLevel(0);
  };

  const fmtSec = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const volBars   = 12;
  const activeBars = Math.round((volumeLevel / 100) * volBars);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="voice-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-300" /> Lançar por voz
          </DialogTitle>
          <DialogDescription>{STATUS[phase] || STATUS.idle}</DialogDescription>
        </DialogHeader>

        {/* ── IDLE ──────────────────────────────────────────────── */}
        {phase === "idle" && (
          <div className="py-6 flex flex-col items-center gap-5">
            <button
              type="button" onClick={startRecording}
              className="relative h-28 w-28 rounded-full grid place-items-center bg-emerald-500/15 border-2 border-emerald-500/60 text-emerald-300 hover:scale-105 transition-transform"
              data-testid="voice-start-btn">
              <Mic className="h-12 w-12" />
            </button>
            <div className="text-center text-xs text-slate-400 space-y-1.5 max-w-xs">
              <p>
                Diga <span className="text-emerald-300 font-semibold">"Monyxx"</span> antes do comando
                para ativar modo avançado
              </p>
              <p className="text-slate-500 italic">"Monyxx gastei 45 reais no mercado no pix"</p>
              <p className="text-slate-600">ou fale diretamente sem a palavra de ativação</p>
            </div>
          </div>
        )}

        {/* ── WAKEWORD LISTENING — subtle green, awaiting "Monyxx" ── */}
        {phase === "wakeword_listening" && (
          <div className="py-6 flex flex-col items-center gap-5">
            <div className="relative h-28 w-28 flex items-center justify-center">
              <span className="absolute inset-0 rounded-full border-2 border-emerald-500/30 animate-ping"
                style={{ animationDuration: "2.5s" }} />
              <button
                type="button" onClick={stopRecording}
                className="absolute inset-0 rounded-full grid place-items-center bg-emerald-500/10 border-2 border-emerald-500/35 text-emerald-400/70 hover:border-emerald-500/55 transition"
                data-testid="voice-stop-btn">
                <Mic className="h-12 w-12" />
              </button>
            </div>
            <div className="text-center">
              <p className="text-sm text-emerald-400/80">
                Aguardando <span className="font-bold text-emerald-300">'Monyxx'</span>...
              </p>
              <p className="text-xs text-slate-500 mt-1">ou fale diretamente para modo normal</p>
            </div>
          </div>
        )}

        {/* ── WAKEWORD ACTIVE — green waves, "Pode falar..." ──────── */}
        {phase === "wakeword_active" && (
          <div className="py-4 flex flex-col items-center gap-4">
            <div className="relative h-36 w-36 flex items-center justify-center">
              {/* Three radiating rings */}
              <span className="absolute inset-0 rounded-full border-2 border-emerald-400 animate-ping opacity-70"
                style={{ animationDuration: "1s" }} />
              <span className="absolute rounded-full border border-emerald-500/45 animate-ping"
                style={{ inset: "-10px", animationDuration: "1s", animationDelay: "0.28s" }} />
              <span className="absolute rounded-full border border-emerald-500/20 animate-ping"
                style={{ inset: "-22px", animationDuration: "1s", animationDelay: "0.55s" }} />
              <button
                type="button" onClick={stopRecording}
                className="relative z-10 h-28 w-28 rounded-full grid place-items-center bg-emerald-500/20 border-2 border-emerald-400 text-emerald-300 transition hover:scale-105"
                data-testid="voice-stop-btn">
                <Mic className="h-12 w-12" />
              </button>
            </div>

            <div className="text-center space-y-1">
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="h-4 w-4 text-emerald-400" />
                <p className="text-base font-semibold text-emerald-300">Pode falar seu lançamento...</p>
                <Sparkles className="h-4 w-4 text-emerald-400" />
              </div>
              <div className="font-mono text-lg text-emerald-400/70">{fmtSec(recordingSec)}</div>
            </div>

            {/* Volume bars — green theme */}
            <div className="flex items-center gap-1 h-7">
              <Volume2 className="h-4 w-4 text-emerald-500/60 mr-1" />
              {Array.from({ length: volBars }).map((_, i) => (
                <div key={i}
                  className={`w-2 rounded-full transition-all duration-75 ${i < activeBars ? "bg-emerald-400" : "bg-slate-800"}`}
                  style={{ height: `${8 + i * 1.5}px` }} />
              ))}
            </div>
          </div>
        )}

        {/* ── NORMAL RECORDING ────────────────────────────────────── */}
        {phase === "recording" && (
          <div className="py-6 flex flex-col items-center gap-4">
            <button
              type="button" onClick={stopRecording}
              className="relative h-28 w-28 rounded-full grid place-items-center bg-rose-500/20 border-2 border-rose-500 text-rose-300"
              data-testid="voice-stop-btn">
              <span className="absolute inset-0 rounded-full border-2 border-rose-500 animate-ping opacity-50" />
              <Square className="h-10 w-10 fill-current" />
            </button>
            <div className="flex flex-col items-center gap-2 w-full max-w-xs">
              <div className="font-mono text-2xl text-rose-300" data-testid="voice-timer">{fmtSec(recordingSec)}</div>
              <div className="flex items-center gap-1 h-6">
                <Volume2 className="h-4 w-4 text-slate-500 mr-1" />
                {Array.from({ length: volBars }).map((_, i) => (
                  <div key={i}
                    className={`w-2 rounded-full transition-all duration-75 ${
                      i < activeBars
                        ? i < volBars * 0.5 ? "bg-emerald-400" : i < volBars * 0.75 ? "bg-amber-400" : "bg-rose-400"
                        : "bg-slate-800"
                    }`}
                    style={{ height: `${8 + i * 1.5}px` }} />
                ))}
              </div>
              <p className="text-[10px] text-slate-500 text-center">Para automaticamente ao detectar 2s de silêncio</p>
            </div>
          </div>
        )}

        {/* ── PROCESSING ──────────────────────────────────────────── */}
        {phase === "processing" && (
          <div className="py-10 flex flex-col items-center gap-4">
            <div className="h-28 w-28 rounded-full bg-slate-800 border-2 border-slate-700 grid place-items-center">
              <Loader2 className="h-12 w-12 text-emerald-400 animate-spin" />
            </div>
            <p className="text-sm text-slate-400 animate-pulse">Transcrevendo e analisando com IA...</p>
          </div>
        )}

        {/* ── RETRY ───────────────────────────────────────────────── */}
        {phase === "retry" && (
          <div className="py-6 flex flex-col items-center gap-5">
            <div className="h-24 w-24 rounded-full bg-amber-500/10 border-2 border-amber-500/40 grid place-items-center text-amber-400">
              <Mic className="h-10 w-10" />
            </div>
            <div className="text-center space-y-1.5">
              <p className="text-base font-semibold text-amber-300">Não entendi, pode repetir?</p>
              <p className="text-xs text-slate-400">Fale mais alto e próximo do microfone</p>
              <p className="text-xs text-slate-500">Lembre-se: diga <span className="text-emerald-300">'Monyxx'</span> antes do lançamento</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={tryAgain} variant="outline" data-testid="voice-retry-btn">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Tentar de novo
              </Button>
              <Button onClick={() => onOpenChange(false)} variant="ghost" data-testid="voice-cancel-btn">
                <X className="h-4 w-4 mr-1.5" /> Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* ── ERROR ───────────────────────────────────────────────── */}
        {phase === "error" && (
          <div className="py-6 flex flex-col items-center gap-4">
            <Button onClick={tryAgain} variant="outline" data-testid="voice-retry-btn">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Tentar novamente
            </Button>
          </div>
        )}

        {/* ── PREVIEW / EDIT FORM ─────────────────────────────────── */}
        {phase === "preview" && form && (
          <div className="space-y-4">
            {transcricao && (
              <div className="rounded-xl bg-slate-900 border border-slate-800 p-3">
                <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">Você disse</div>
                <div className="text-sm text-slate-200 italic" data-testid="voice-transcricao">"{transcricao}"</div>
                {confianca != null && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Confiança da IA</div>
                    <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div
                        className={`h-full ${confianca >= 0.7 ? "bg-emerald-500" : confianca >= 0.4 ? "bg-amber-500" : "bg-rose-500"}`}
                        style={{ width: `${Math.round(confianca * 100)}%` }} />
                    </div>
                    <div className="text-xs font-mono text-slate-300">{Math.round(confianca * 100)}%</div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <Sparkles className="h-3.5 w-3.5 text-emerald-400" />
              <span className="text-xs text-emerald-300">
                Será salvo em:{" "}
                <strong>
                  {form.tipo === "entrada" ? "Entradas"
                    : form.tipo === "fixo"    ? "Gastos Fixos"
                    : form.tipo === "cartao"  ? "Cartões de Crédito"
                    : "Gastos Variáveis"}
                </strong>
              </span>
            </div>

            {/* Tipo */}
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tipo</label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {TIPOS.map((t) => (
                  <button key={t.id} type="button" onClick={() => onTipoChange(t.id)}
                    className={`px-3 py-2 rounded-xl text-sm font-semibold border transition ${
                      form.tipo === t.id ? t.on : "border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                    data-testid={`voice-tipo-${t.id}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Card selector — only when tipo is cartao */}
            {form.tipo === "cartao" && (
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Cartão</label>
                {appData.cartoes?.length === 0 ? (
                  <div className="mt-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    Nenhum cartão cadastrado.{" "}
                    <button onClick={() => { onOpenChange(false); navigate("/app/cartoes"); }}
                      className="underline hover:text-amber-300">
                      Cadastrar cartão
                    </button>
                  </div>
                ) : (
                  <select
                    value={form.cartaoId || ""}
                    onChange={(e) => setForm({ ...form, cartaoId: e.target.value })}
                    className="mt-1.5 w-full h-10 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm focus:outline-none focus:border-emerald-500/40"
                    data-testid="voice-input-cartao">
                    {appData.cartoes.map((c) => (
                      <option key={c.id} value={c.id}>{c.nome || c.bandeira || `Cartão ${c.id}`}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Descrição */}
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Descrição</label>
              <Input value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })}
                className="mt-1.5" data-testid="voice-input-desc" />
            </div>

            {/* Valor + Data */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Valor (R$)</label>
                <Input type="text" inputMode="decimal" value={form.valor}
                  onChange={(e) => setForm({ ...form, valor: e.target.value })}
                  className="mt-1.5" data-testid="voice-input-valor" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Data</label>
                <Input type="date" value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                  className="mt-1.5" data-testid="voice-input-data" />
              </div>
            </div>

            {/* Pagamento + Categoria */}
            <div className={`grid ${form.tipo === "entrada" || form.tipo === "cartao" ? "grid-cols-1" : "grid-cols-2"} gap-3`}>
              {form.tipo !== "cartao" && (
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Pagamento</label>
                  <select value={form.pagamento} onChange={(e) => setForm({ ...form, pagamento: e.target.value })}
                    className="mt-1.5 w-full h-10 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm focus:outline-none focus:border-emerald-500/40"
                    data-testid="voice-input-pagamento">
                    {PAGAMENTOS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              )}
              {form.tipo !== "entrada" && (
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Categoria</label>
                  <select value={form.cat || "eventuais"} onChange={(e) => setForm({ ...form, cat: e.target.value })}
                    className="mt-1.5 w-full h-10 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm focus:outline-none focus:border-emerald-500/40"
                    data-testid="voice-input-categoria">
                    {getCategories(appData?.customCategories).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Parcelas — always visible for cartão; for other tipos only when AI detected */}
            {(form.tipo === "cartao" || form.parcelas) && (
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Parcelas</label>
                <Input type="number" min="1" max="60" value={form.parcelas ?? 1}
                  onChange={(e) => setForm({ ...form, parcelas: Number(e.target.value) })}
                  className="mt-1.5" data-testid="voice-input-parcelas" />
                {form.tipo === "cartao" && Number(form.parcelas) > 1 && form.valor && (
                  <p className="mt-1.5 text-xs text-slate-400">
                    {Number(form.parcelas)}x de{" "}
                    <span className="text-emerald-300 font-semibold">
                      R${" "}
                      {(Number(String(form.valor).replace(",", ".")) / Number(form.parcelas))
                        .toFixed(2).replace(".", ",")}
                    </span>
                    {" "}por parcela
                  </p>
                )}
              </div>
            )}

            {/* Pago + Repetir — hidden for cartao (handled by card billing cycle) */}
            {form.tipo !== "cartao" && (
              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.pago}
                    onChange={(e) => setForm({ ...form, pago: e.target.checked })}
                    className="h-4 w-4 accent-emerald-500" data-testid="voice-input-pago" />
                  <span className="text-slate-300">Já {form.tipo === "entrada" ? "recebido" : "pago"}</span>
                </label>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.repetir}
                    onChange={(e) => setForm({ ...form, repetir: e.target.checked })}
                    className="h-4 w-4 accent-emerald-500" data-testid="voice-input-repetir" />
                  <span className="text-slate-300">Repetir nos próximos meses</span>
                </label>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={tryAgain} className="flex-1" data-testid="voice-redo-btn">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Refazer
              </Button>
              <Button type="button" onClick={() => onOpenChange(false)} variant="ghost" className="flex-1" data-testid="voice-cancel-btn">
                <X className="h-4 w-4 mr-1.5" /> Cancelar
              </Button>
              <Button type="button" onClick={confirm} className="flex-1 monyx-btn-primary text-white" data-testid="voice-save-btn">
                <Save className="h-4 w-4 mr-1.5" /> OK, salvar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
